// Audit log, newest first, paged by ID, with an actor filter (docs/api.md 2.1, ADR-0028) and a time window.
import { useEffect, useState } from "react";
import type { AuditEntry, TenantDetail } from "../api/types";
import { Badge } from "../components/Badge";
import { useServices } from "../components/context";
import { Id } from "../components/IdCell";
import { ErrorBanner, Section, useAction } from "../components/ui";
import {
  AUDIT_PRESETS,
  auditCursor,
  auditWindow,
  inWindow,
  type AuditPreset,
  type AuditWindow,
} from "../lib/auditWindow";
import { formatTime } from "../lib/format";
import { atLeast } from "../lib/roles";

const PAGE = 50;

export function Audit({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [actor, setActor] = useState<"" | "user" | "principal">("");
  const [preset, setPreset] = useState<AuditPreset>("all");
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  // The custom inputs take effect on "Apply", so typing does not reload the list.
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [win, setWin] = useState<AuditWindow>({ from: null, to: null });
  const [winError, setWinError] = useState<string | null>(null);
  const load = useAction();
  const allowed = atLeast(tenant.role, "admin");

  /** Loads the first page (no `before`) or the next page. Entries older than the window end the paging. */
  async function fetchPage(before?: string) {
    let w = win;
    if (!before) {
      const r = auditWindow(preset, Date.now(), custom);
      if ("error" in r) {
        setWinError(r.error);
        return;
      }
      setWinError(null);
      w = r;
      setWin(r);
    }
    await load.run(async () => {
      const cursor = before ?? (w.to !== null ? auditCursor(w.to) : undefined);
      const page = await api.listAudit(tenant.slug, {
        limit: PAGE,
        before: cursor,
        actorKind: actor || undefined,
      });
      const shown = page.items.filter((a) => inWindow(a.created_at, w));
      const reachedStart =
        w.from !== null && page.items.some((a) => Date.parse(a.created_at) < (w.from as number));
      setItems((cur) => (before ? [...cur, ...shown] : shown));
      setNext(reachedStart ? null : page.next_before);
      setLoaded(true);
    });
  }

  useEffect(() => {
    if (allowed) void fetchPage();
    // Load again when the tenant, the actor filter or the time window changes.
  }, [tenant.slug, allowed, actor, preset, custom]);

  if (!allowed) {
    return (
      <Section title="Audit log">
        <p className="muted">Only admins and owners can see the audit log.</p>
      </Section>
    );
  }

  return (
    <Section
      title="Audit log"
      actions={
        <div className="row">
          <label className="inline">
            Actor{" "}
            <select value={actor} onChange={(e) => setActor(e.target.value as "" | "user" | "principal")}>
              <option value="">All</option>
              <option value="user">Users (humans)</option>
              <option value="principal">Principals (machines)</option>
            </select>
          </label>
          <label className="inline">
            Time{" "}
            <select value={preset} onChange={(e) => setPreset(e.target.value as AuditPreset)}>
              {AUDIT_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="secondary" disabled={load.busy} onClick={() => void fetchPage()}>
            Refresh
          </button>
        </div>
      }
    >
      {preset === "custom" && (
        <form
          className="row audit-window"
          onSubmit={(e) => {
            e.preventDefault();
            setCustom({ from: fromInput, to: toInput });
          }}
        >
          <label className="inline">
            From (UTC){" "}
            <input type="datetime-local" value={fromInput} onChange={(e) => setFromInput(e.target.value)} />
          </label>
          <label className="inline">
            To (UTC){" "}
            <input type="datetime-local" value={toInput} onChange={(e) => setToInput(e.target.value)} />
          </label>
          <button type="submit" className="secondary">
            Apply
          </button>
        </form>
      )}
      {winError && (
        <p className="banner error" role="alert">
          {winError}
        </p>
      )}
      {(win.from !== null || win.to !== null) && !winError && (
        <p className="muted small" data-testid="audit-window">
          Showing entries from{" "}
          {win.from !== null ? formatTime(new Date(win.from).toISOString()) : "the start"} to{" "}
          {win.to !== null ? formatTime(new Date(win.to).toISOString()) : "now"}.
        </p>
      )}
      <ErrorBanner error={load.error} />
      <table className="data-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Action</th>
            <th>Result</th>
            <th>Actor</th>
            <th>Target</th>
            <th>Request</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id}>
              <td>{formatTime(a.created_at)}</td>
              <td className="mono">{a.action}</td>
              <td>
                <span className={`tag ${a.result}`}>{a.result}</span>
              </td>
              <td className="id-cell" data-label="Actor">
                <Badge kind={a.actor_kind} /> {a.actor_id && <Id id={a.actor_id} />}
              </td>
              <td className="mono small">{a.target ?? ""}</td>
              <td
                className="id-cell"
                data-label="Request"
                title={a.detail ? JSON.stringify(a.detail) : undefined}
              >
                {a.request_id && <Id id={a.request_id} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loaded && items.length === 0 && (
        <p className="muted">
          {win.from !== null || win.to !== null
            ? "No audit entries in this time window."
            : "No audit entries yet."}
        </p>
      )}
      {next && (
        <button type="button" className="secondary" disabled={load.busy} onClick={() => void fetchPage(next)}>
          Load older entries
        </button>
      )}
    </Section>
  );
}
